import React, { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Toast from '../components/ui/SimpleToast';

// Create context with default fallback value
const ToastContext = createContext({
  addToast: () => {},
  removeToast: () => {},
  showSuccess: () => {},
  showError: () => {},
  showWarning: () => {},
  showInfo: () => {},
  clearAllToasts: () => {},
});

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context || typeof context.showSuccess !== 'function') {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(toast => {
    const id = Date.now().toString();
    const newToast = {
      id,
      ...toast,
    };
    setToasts(prev => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback(id => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback(
    (message, title = 'Success') => {
      return addToast({
        type: 'success',
        title,
        message,
      });
    },
    [addToast]
  );

  const showError = useCallback(
    (message, title = 'Error') => {
      return addToast({
        type: 'error',
        title,
        message,
        delay: 10000,
      });
    },
    [addToast]
  );

  const showWarning = useCallback(
    (message, title = 'Warning') => {
      return addToast({
        type: 'warning',
        title,
        message,
      });
    },
    [addToast]
  );

  const showInfo = useCallback(
    (message, title = 'Info') => {
      return addToast({
        type: 'info',
        title,
        message,
      });
    },
    [addToast]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    addToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1055 }}>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            id={toast.id}
            title={toast.title}
            message={toast.message}
            type={toast.type}
            autoHide={toast.autoHide}
            delay={toast.delay}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
